import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, CheckCircle, Send, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { investmentOptions } from '../../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const normalizePhone = (phone) => {
  let digits = phone.replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) digits = digits.slice(2);
  else if (digits.startsWith('0') && digits.length === 11) digits = digits.slice(1);
  return digits;
};

const validatePhone = (phone) => {
  if (phone.length !== 10) return false;
  if (!/^\d+$/.test(phone)) return false;
  if (!['6', '7', '8', '9'].includes(phone[0])) return false;
  return true;
};

const getUtmParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
  };
};

const EnquiryModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    investment_interest: '',
    message: '',
    website_url: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [investmentError, setInvestmentError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (document.getElementById('turnstile-script')) return;
    const script = document.createElement('script');
    script.id = 'turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId.current); } catch (err) { /* ignore */ }
      }
    };
  }, []);

  const renderTurnstile = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current || !window.turnstile) return;
    if (turnstileWidgetId.current) {
      try { window.turnstile.remove(turnstileWidgetId.current); } catch (err) { /* ignore */ }
    }
    try {
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
        theme: 'light',
        size: 'flexible',
      });
    } catch (e) { console.error('[Form] CAPTCHA error:', e); }
  }, []);

  useEffect(() => {
    if (isOpen && submitStatus === null && TURNSTILE_SITE_KEY) {
      const timer = setTimeout(() => {
        if (window.turnstile) renderTurnstile();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, submitStatus, renderTurnstile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'phone') setPhoneError('');
    if (name === 'email') setEmailError('');
    if (name === 'full_name') setNameError('');
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({ ...prev, investment_interest: value }));
    setInvestmentError('');
  };

  const resetForm = () => {
    setFormData({ full_name: '', phone: '', email: '', city: '', investment_interest: '', message: '', website_url: '' });
    setTurnstileToken('');
    setPhoneError('');
    setEmailError('');
    setNameError('');
    setInvestmentError('');
    if (window.turnstile && turnstileWidgetId.current) {
      try { window.turnstile.reset(turnstileWidgetId.current); } catch (err) { /* ignore */ }
    }
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!formData.full_name.trim() || formData.full_name.trim().length < 2) {
      setNameError('Name is required');
      isValid = false;
    }
    
    const phoneNormalized = normalizePhone(formData.phone);
    if (!validatePhone(phoneNormalized)) {
      setPhoneError('Valid 10-digit phone required');
      isValid = false;
    }
    
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setEmailError('Valid email is required');
      isValid = false;
    }
    
    if (!formData.investment_interest) {
      setInvestmentError('Please select investment range');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    
    if (!validateForm()) return;
    
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setErrorMessage('Please complete the security check');
      setSubmitStatus('error');
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    setSubmitStatus(null);
    
    const phoneNormalized = normalizePhone(formData.phone);
    const utmParams = getUtmParams();
    const rawPayload = {
      full_name: formData.full_name.trim(),
      phone_raw: formData.phone.trim(),
      phone_normalized: phoneNormalized,
      email: formData.email.trim(),
      city: formData.city.trim(),
      investment_interest: formData.investment_interest,
      message: formData.message.trim(),
    };
    const payload = {
      full_name: formData.full_name.trim(),
      phone_raw: formData.phone.trim(),
      phone_normalized: phoneNormalized,
      email: formData.email.trim(),
      interest_type: formData.investment_interest,
      quota_or_plan: formData.investment_interest,
      investment_interest: formData.investment_interest,
      city: formData.city.trim(),
      message: formData.message.trim(),
      page_url: window.location.href,
      submitted_at: new Date().toISOString(),
      user_agent: navigator.userAgent,
      ...utmParams,
      turnstile_token: turnstileToken,
      website_url: formData.website_url,
      raw_payload: rawPayload,
    };
    
    try {
      const response = await axios.post(`${API}/enquiry`, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 30000,
      });
      if (response.data.ok) {
        setSubmitStatus('success');
        resetForm();
      } else {
        throw new Error(response.data.error || 'Submission failed');
      }
    } catch (error) {
      let errorMsg = 'Something went wrong. Please try again.';
      if (error.response) {
        const { data, status } = error.response;
        if (status === 429) errorMsg = 'Too many requests. Please wait.';
        else if (data?.error === 'captcha_failed') errorMsg = 'Security check failed.';
        else if (data?.error === 'invalid_phone') { errorMsg = 'Invalid phone number.'; setPhoneError('Invalid'); }
        else if (data?.error === 'invalid_name') errorMsg = 'Please enter your full name.';
      }
      setErrorMessage(errorMsg);
      setSubmitStatus('error');
      if (window.turnstile && turnstileWidgetId.current) {
        try { window.turnstile.reset(turnstileWidgetId.current); setTurnstileToken(''); } catch (err) { /* ignore */ }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewEnquiry = () => {
    setSubmitStatus(null);
    setErrorMessage('');
    resetForm();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-600" />
        </button>

        <div className="p-8 md:p-10">
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#004534] mb-3">Thank You!</h3>
              <p className="text-gray-500 text-lg mb-2">We have received your enquiry.</p>
              <p className="text-gray-500 text-lg mb-6">Our team will contact you within 24 hours.</p>
              <p className="text-[#004534] font-semibold mb-8">
                Call us: <a href="tel:+916292050222" className="text-[#0C6951] underline">+91 6292050222</a>
              </p>
              <div className="flex gap-3 justify-center">
                <Button onClick={handleNewEnquiry} variant="outline" className="rounded-full border-[#004534] text-[#004534] px-6 py-3">
                  Submit Another
                </Button>
                <Button onClick={onClose} className="rounded-full bg-[#004534] text-white px-6 py-3">
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-[#004534] mb-2">Get Started</h2>
                <p className="text-gray-500 text-lg">Fill in your details and we will reach out within 24 hours.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {submitStatus === 'error' && errorMessage && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-red-700 text-base">{errorMessage}</p>
                  </div>
                )}

                <div className="absolute left-[-9999px]" aria-hidden="true">
                  <input type="text" name="website_url" value={formData.website_url} onChange={handleInputChange} tabIndex={-1} autoComplete="off" />
                </div>

                {/* Name - Required */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                    className={`rounded-xl h-14 text-lg ${nameError ? 'border-red-400' : 'border-gray-200'}`}
                    disabled={isSubmitting}
                  />
                  {nameError && <p className="text-red-500 text-sm mt-1">{nameError}</p>}
                </div>

                {/* Phone - Required */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="+91 98765 43210"
                    className={`rounded-xl h-14 text-lg ${phoneError ? 'border-red-400' : 'border-gray-200'}`}
                    disabled={isSubmitting}
                  />
                  {phoneError && <p className="text-red-500 text-sm mt-1">{phoneError}</p>}
                </div>

                {/* Email - Required */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your@email.com"
                    className={`rounded-xl h-14 text-lg ${emailError ? 'border-red-400' : 'border-gray-200'}`}
                    disabled={isSubmitting}
                  />
                  {emailError && <p className="text-red-500 text-sm mt-1">{emailError}</p>}
                </div>

                {/* Investment Range - Required */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    Investment Range <span className="text-red-500">*</span>
                  </label>
                  <Select onValueChange={handleSelectChange} value={formData.investment_interest} disabled={isSubmitting}>
                    <SelectTrigger className={`rounded-xl h-14 text-lg ${investmentError ? 'border-red-400' : 'border-gray-200'}`}>
                      <SelectValue placeholder="Select your investment range" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {investmentOptions.map((option, index) => (
                        <SelectItem key={index} value={option} className="text-base py-3">{option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {investmentError && <p className="text-red-500 text-sm mt-1">{investmentError}</p>}
                </div>

                {/* City - Optional */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    City <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <Input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Your city"
                    className="rounded-xl border-gray-200 h-14 text-lg"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Message - Optional */}
                <div>
                  <label className="block text-base font-semibold text-[#004534] mb-2">
                    Message <span className="text-gray-400 font-normal">(Optional)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Any questions or comments?"
                    className="w-full rounded-xl border border-gray-200 p-4 text-lg resize-none focus:border-[#004534] focus:ring-1 focus:ring-[#004534]"
                    disabled={isSubmitting}
                  />
                </div>

                {TURNSTILE_SITE_KEY && <div className="flex justify-center"><div ref={turnstileRef} /></div>}

                <Button
                  type="submit"
                  disabled={isSubmitting || (TURNSTILE_SITE_KEY && !turnstileToken)}
                  className="w-full bg-[#004534] hover:bg-[#0C6951] text-white font-bold rounded-full h-14 text-lg transition-all hover:scale-[1.02] disabled:opacity-60"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>
                      Submitting...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" /> Submit Enquiry
                    </span>
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiryModal;
